import React, { FC, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDanceStore } from '../store/useDanceStore';
import { X, Check, AlertCircle } from 'lucide-react';
import { DanceState } from '../types';

export const InputDialog: FC = () => {
  const { t } = useTranslation();
  const inputDialog = useDanceStore((state: DanceState) => state.inputDialog);
  const closeInputDialog = useDanceStore((state: DanceState) => state.closeInputDialog);
  
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputDialog?.isOpen) {
      setValue(inputDialog.defaultName);
      setLoading(false);
      setErrorMsg(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [inputDialog?.isOpen, inputDialog?.defaultName]);

  if (!inputDialog || !inputDialog.isOpen) return null;

  const { title, onSave, onCancel, placeholder, submitLabel, isPassword, folderName, notification } = inputDialog;

  const handleSave = async () => {
    if (isPassword || value.trim()) {
      setLoading(true);
      setErrorMsg(null);
      try {
        await onSave(isPassword ? value : value.trim());
        closeInputDialog();
      } catch (err: any) {
        console.error("Error saving input dialog:", err);
        setErrorMsg(err.message || "An error occurred");
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    if (loading) return;
    if (onCancel) onCancel();
    closeInputDialog();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (loading) return;
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className="input-dialog-overlay">
      <div className="glass-panel input-dialog-content">
        <div className="flex-between input-dialog-header">
          <div className="input-dialog-header-left">
            <h2 className="input-dialog-title">{title}</h2>
          </div>
          <button 
            onClick={handleCancel} 
            className="input-dialog-close-btn" 
            disabled={loading}
            style={loading ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <X size={24} />
          </button>
        </div>
        
        {folderName && (
          <div className="input-dialog-folder">
            <span className="input-dialog-folder-label">{t('inputDialog.folder_label')}</span>
            <span className="input-dialog-folder-name">{folderName}</span>
          </div>
        )}

        {notification && (
          <div className="input-dialog-notification">
            <AlertCircle className="input-dialog-notification-icon" size={16} />
            <span className="input-dialog-notification-text">{notification}</span>
          </div>
        )}
        <div className="input-dialog-body">
          <input
            ref={inputRef}
            type={isPassword ? 'password' : 'text'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || t('library.enter_folder_name')}
            className="input-dialog-input"
            disabled={loading}
          />
          {errorMsg && (
            <div className="input-dialog-error" style={{ color: '#f87171', fontSize: '0.8rem', marginTop: '8px', padding: '0 4px', textAlign: 'left' }}>
              {errorMsg}
            </div>
          )}
        </div>
        
        <div className="input-dialog-footer">
          <button 
            onClick={handleCancel}
            className="input-dialog-btn-cancel"
            disabled={loading}
            style={loading ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            {t('common.cancel')}
          </button>
          <button 
            className="primary input-dialog-btn-submit" 
            onClick={handleSave} 
            disabled={loading || (!isPassword && !value.trim())}
            style={loading ? { opacity: 0.8, cursor: 'wait' } : {}}
          >
            {loading ? (
              <div className="spinner" style={{ width: '16px', height: '16px', borderTopColor: '#fff', borderLeftColor: 'transparent', marginRight: '8px', display: 'inline-block' }} />
            ) : (
              <Check size={18} />
            )}
            {loading ? 'Generating...' : (submitLabel || t('common.confirm'))}
          </button>
        </div>
      </div>
    </div>
  );
};
